/**
 * Les tableaux de bord opérationnels des responsables — PRD §4.7.1.
 *
 * *Demande de Chris, 15/08/2026 : « le responsable transport doit avoir tous les
 * transports sur les 3 prochaines semaines avec un système où il peut affecter les
 * ressources réelles en face de ce qui est prévu dans le planning général ; idem pour le
 * responsable exploitation […] ; tableau de bord responsable carrières aussi, qui affiche
 * par chantier par jour ce qu'il doit avoir à dispo sur les trois prochaines semaines. »*
 *
 * ## Trois onglets, une seule source
 *
 * Les trois lisent la **même** réponse du planning général et n'en gardent que ce qui les
 * concerne. Trois appels séparés auraient fini par annoncer deux jours différents pour la
 * même rotation, et rien à l'écran n'aurait dit lequel avait raison.
 *
 * ## Ce que les carrières ne font pas
 *
 * Elles n'affectent rien. Le responsable carrières lit ce qu'il doit tenir à disposition
 * et organise sa production ; les camions qui viennent chercher sont l'affaire du
 * transport, et les hommes celle de l'exploitation. Un tableau qui proposerait les trois
 * gestes ferait croire qu'il y a trois choses à faire là où il y en a une.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { dec, formaterDateLongue, formaterHeure, formaterNombre } from "@planif/moteur";
import {
  affecterRessource,
  chargerCarrieres,
  chargerGeneriques,
  chargerIndisponibilites,
  adresseQrMateriel,
  chargerMateriels,
  chargerPlan,
  chargerSalaries,
  retirerAffectationReelle,
} from "./api";
import type {
  BesoinDate,
  Carriere,
  ChantierAuPlan,
  Materiel,
  PlanGeneral,
  Salarie,
} from "./api";
import { expliquerLAbsenceDeKm } from "@planif/moteur";
import type { Generique } from "@planif/moteur";
import { PanneauPeriode } from "./Periode";
import type { Bornes } from "./Periode";
import { TableauCarrieres } from "./TableauCarrieres";
import { AbsencesAGerer, direLAbsence } from "./AbsencesAGerer";
import { AnomaliesAffectation } from "./AnomaliesAffectation";
import { FacturationPrevisionnelle } from "./FacturationPrevisionnelle";
import { MouvementsMateriel } from "./MouvementsMateriel";
import type { Absence } from "./AbsencesAGerer";
import { designerMateriel } from "./designerRessource";
import { adresseDuPlanning, NOM_FENETRE } from "./PlanningRessources";
import {
  amenerSousLesYeux,
  ancreDuPlan,
  ancresDeLaCible,
  ATTRIBUT_CIBLE,
  cleDuBesoin,
} from "./cibles";
import type { CibleDuPlanDeCharge } from "./cibles";
import {
  choisirCandidats,
  familleDeLaMachine,
  ouvrirLeReferentiel,
  tousLesCandidats,
} from "./candidatsAffectation";
import { direLAssociation } from "./associationPosee";
import { direLaSerie } from "./serieDeJours";
import { direLaProvenance, estParNosSoins } from "./livraisonParNosSoins";
import { direLaRepartition, direLeMomentDeLEcheance } from "./heuresParMoment";
import {
  aQuelqueChoseADire,
  direLEcart,
  direLeCalcul,
  direLIncapacite,
  tonDeLEcart,
} from "./controleTransport";
import type { Proposition } from "./candidatsAffectation";

/** Ce que la liste d'affectation a besoin de savoir d'une ressource réelle. */
type CandidatAffectable = {
  readonly id: string;
  readonly libelle: string;
  readonly nature: "salarie" | "materiel";
  readonly absent: boolean;
};

type Onglet = "transport" | "exploitation" | "carrieres" | "facturation" | "anomalies";

const TITRES: Record<Onglet, string> = {
  transport: "Transport",
  exploitation: "Exploitation",
  carrieres: "Carrières",
  facturation: "Facturation prévisionnelle",
  anomalies: "Anomalies affectation",
};

const EXPLICATIONS: Record<Onglet, string> = {
  transport:
    "Toutes les rotations planifiées, jour par jour. Affectez le camion ou le chauffeur qui " +
    "assurera chacune — le besoin vient du planning général, il bouge avec lui.",
  exploitation:
    "Le besoin en hommes et en machines, jour par jour. Affectez les personnes et le matériel " +
    "réels en face de ce que la contre-étude a chiffré.",
  carrieres:
    "Ce qu'il faut tenir à disposition, par chantier et par jour. Lecture seule : les tournées " +
    "sont l'affaire du transport.",
  /*
    L'explication des anomalies vit dans l'écran lui-même, avec le délai de deux jours qu'elle
    doit énoncer : ce bandeau-là sert aussi de support au filtre par affaire, qui n'a pas de sens
    sur un tableau trié par échéance. Elle est déclarée ici quand même — le type l'exige, et un
    `Record` incomplet serait la porte par laquelle un onglet arriverait un jour sans un mot.
  */
  facturation:
    "Le carnet de commandes daté, mois par mois : ce qui était prévu à la validation face à ce " +
    "que la contre-étude en cours annonce aujourd'hui.",
  anomalies:
    "Ce qui empêchera les saisies de chantier de remonter juste, trié par échéance.",
};

/**
 * Comment se dit un moment de la journée, dans un signalement.
 *
 * *Demande de Chris, 15/08/2026 : « ressource déjà utilisée matin ou/et après-midi sur
 * chantier X ».* Les positions vides ne se traduisent pas par « — » : une rotation de
 * transport n'a pas de demi-journée, elle a une heure limite. Dire « dans la journée » est
 * exact ; laisser un tiret ferait chercher une donnée manquante.
 */
function direLesMoments(positions: readonly ("matin" | "apres_midi")[]): string {
  const dits = positions.map((p) => (p === "matin" ? "le matin" : "l'après-midi"));
  if (dits.length === 0) return "dans la journée";
  if (dits.length === 1) return dits[0] ?? "";
  return "toute la journée";
}

/**
 * Combien d'exemplaires ce besoin attend, en toutes lettres — ou rien.
 *
 * *Signalé par le métier le 16/08/2026 : « j'ai mis 14,19 h de chauffeur et j'ai affecté
 * 4 chauffeurs, mais je vois une ligne de 3,5 h sans savoir qu'il y a 4 chauffeurs à
 * affecter ; idem pour le matériel ».*
 *
 * L'écran écrivait `×4`, ce qui suppose de savoir ce que le nombre multiplie — et sur une
 * ligne à un seul exemplaire il écrivait `×1`, du bruit sur la quasi-totalité des lignes.
 * Il dit maintenant **ce qu'il y a à faire** : quatre ressources à affecter. Rien en
 * dessous de deux, parce qu'un exemplaire est le cas normal et que l'annoncer ferait
 * chercher ce qui le distingue des autres.
 *
 * Volontairement sans nom de métier — « 4 chauffeurs » supposerait de savoir lire un code
 * du référentiel, et la même ligne porte des hommes ou des machines selon le code.
 */
export function direLEffectif(unites: number | null): string | null {
  return unites !== null && unites > 1 ? `${String(unites)} à affecter` : null;
}

/**
 * Ce que la colonne « Affecté » annonce : combien sur combien.
 *
 * `complet` est faux tant qu'il manque quelqu'un — c'est lui qui décide de la couleur. Un
 * besoin de quatre chauffeurs dont un seul est nommé n'est pas couvert, et un tableau qui
 * l'affiche comme les autres laisse partir le chantier avec trois places vides.
 */
export function direLaCouverture(
  affectes: number,
  attendus: number | null,
): { readonly texte: string; readonly complet: boolean } {
  const besoin = attendus !== null && attendus > 1 ? attendus : 1;
  if (affectes === 0) {
    return { texte: besoin > 1 ? `personne sur ${String(besoin)}` : "personne", complet: false };
  }
  // Un seul exemplaire attendu : la liste des noms dit déjà tout, et « 1 / 1 » l'alourdit.
  if (besoin === 1) return { texte: "", complet: true };
  return {
    texte: `${String(affectes)} / ${String(besoin)} affecté(s)`,
    complet: affectes >= besoin,
  };
}

/** Ce qu'une affaire a d'affecté et ce qui lui manque, sur la période et l'onglet regardés. */
export type Couverture = {
  /** Combien de besoins l'affaire porte. Zéro = rien à affecter, donc ni vert ni rouge. */
  readonly besoins: number;
  /** Combien de besoins ne sont pas couverts. Zéro et `besoins > 0` = tout est affecté. */
  readonly aAffecter: number;
};

/**
 * Ce qui manque à affecter, affaire par affaire.
 *
 * *Demande de Chris, 03/09/2026 : « dans le menu déroulant pour sélectionner les affaires, il
 * faudrait faire apparaître en vert quand tout est affecté et en rouge s'il manque des
 * affectations ; ceci alertera le responsable pour lui dire qu'il doit affecter les ressources. »*
 *
 * ## La règle de couverture est celle de `direLaCouverture`, et pas une seconde
 *
 * Un besoin est couvert quand il porte autant de ressources que d'exemplaires déclarés — un
 * seul quand personne n'a rien déclaré. C'est déjà ce que le tableau écrit ligne par ligne
 * (« 2 / 4 affecté(s) »), et le recompter autrement ferait un menu qui annonce vert au-dessus
 * de lignes rouges.
 *
 * ## Ce qui entre dans le compte dépend de l'ONGLET, et c'est essentiel
 *
 * Les besoins passés ici sont déjà filtrés par onglet : le transport compte ses rotations,
 * l'exploitation ses hommes et ses machines. Un compte commun ferait passer l'exploitation au
 * rouge parce qu'un camion n'est pas affecté — et le responsable chercherait dans son écran
 * quelque chose qui ne s'y trouve pas.
 *
 * *Une affaire sans aucun besoin sur la période reste **neutre** : « tout est affecté » sur un
 * chantier qui ne demande rien serait une bonne nouvelle inventée.*
 */
export function couvertureParAffaire(
  besoins: readonly BesoinDate[],
): ReadonlyMap<string, Couverture> {
  const parAffaire = new Map<string, { besoins: number; aAffecter: number }>();
  for (const besoin of besoins) {
    /*
      **Ce qui ne s'affecte pas ne compte pas dans « à affecter ».**

      Les livraisons entrent dans le tableau de bord transport depuis le 03/09/2026, en
      information : aucune ressource ne se pose dessus. Les compter ici laisserait le menu des
      affaires rouge pour toujours, avec un nombre que rien ne permettrait de faire descendre —
      l'alerte cesserait d'en être une.
    */
    if (besoin.nature === "livraison") continue;
    const compte = parAffaire.get(besoin.chantierId) ?? { besoins: 0, aAffecter: 0 };
    compte.besoins += 1;
    if (!direLaCouverture(besoin.affectations.length, besoin.unites).complet) {
      compte.aAffecter += 1;
    }
    parAffaire.set(besoin.chantierId, compte);
  }
  return parAffaire;
}

/**
 * Ce que le menu déroulant ajoute au nom d'une affaire, et de quelle couleur.
 *
 * Le **texte** porte l'information, la couleur ne fait que la rendre visible de loin : la
 * couleur d'une `<option>` n'est pas rendue par tous les systèmes, et « il manque quelque
 * chose » ne doit pas dépendre de ça. C'est aussi ce qui distingue « 7 à affecter » d'un rouge
 * qu'on ne saurait pas interpréter.
 */
export function direLaCharge(couverture: Couverture | undefined): {
  readonly suffixe: string;
  readonly etat: "complet" | "manque" | "neutre";
} {
  if (couverture === undefined || couverture.besoins === 0) {
    return { suffixe: "", etat: "neutre" };
  }
  if (couverture.aAffecter === 0) return { suffixe: " · tout est affecté", etat: "complet" };
  return {
    suffixe: ` · ${String(couverture.aAffecter)} à affecter`,
    etat: "manque",
  };
}

/**
 * L'étiquette QR d'une machine affectée — miniature, agrandie au clic.
 *
 * *Demande de Chris, 16/08/2026 : « afficher le QR code miniature pour les lignes de
 * matériel, afin que le responsable exploitation puisse consulter le détail de la fiche ».*
 *
 * ## Elle vit sur l'AFFECTATION, pas sur la ligne de besoin
 *
 * Une ligne de besoin porte un code **générique** — `04-03-15-700`, « PELLE 20t » —, qui ne
 * désigne aucune machine et n'a donc aucune fiche. C'est l'affectation qui nomme un engin
 * réel, `04-03-15-705`, et lui seul a une fiche à consulter. Poser l'étiquette sur la ligne
 * ferait promettre un détail qui n'existe pas.
 *
 * ## Deux tailles, et la petite ne sert pas à scanner
 *
 * Un QR d'une quarantaine de pixels n'est pas lisible par un téléphone : à cette taille un
 * module fait un pixel. La miniature est un **repère** — elle dit qu'il y a une fiche
 * derrière —, et le clic l'agrandit à une taille réellement scannable. Les deux images ont
 * la même adresse, donc la seconde sort du cache du navigateur.
 *
 * ## Pourquoi une `<img>` vers NOTRE serveur
 *
 * Une `<img src>` ne sait pas porter d'en-tête `Authorization` : elle ne peut donc pas
 * appeler le référentiel, dont l'accès demande une clé — et poser cette clé dans le
 * navigateur la rendrait publique. Elle appelle le relais, sur le même domaine, avec le
 * cookie de session que le navigateur joint tout seul. Aucun état à tenir, aucun appel à
 * ordonnancer : le navigateur cache l'image comme n'importe quelle autre.
 */
/**
 * L'étiquette QR d'une machine affectée, **fabriquée par Planif** depuis le 20/08/2026.
 *
 * Elle était jusque-là relayée depuis archive-roy, et encodait donc l'adresse du référentiel
 * documentaire : scanée, elle ouvrait une application où un mécano n'a pas de compte. Elle encode
 * désormais `planif-roy.app/materiel/<code ERP>` — la même que celle de l'écran du parc, la même
 * que celle des planches imprimées. Une machine, une étiquette.
 *
 * Conséquence sur la clé d'accès : le périmètre `materiels_qrcode` n'est plus nécessaire.
 * `onRefus` reste là pour le cas où le parc lui-même n'est pas lisible.
 */
function EtiquetteQr({
  materielId,
  baseQr,
  libelle,
  onRefus,
}: {
  materielId: string;
  baseQr: string;
  libelle: string;
  onRefus: () => void;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const adresse = adresseQrMateriel(materielId, baseQr);

  return (
    <>
      <button
        type="button"
        className="qr-materiel"
        title={`Étiquette QR de ${libelle} — cliquer pour l'agrandir, puis scanner pour ouvrir sa fiche`}
        onClick={() => {
          setOuvert(!ouvert);
        }}
      >
        {/*
          Une image cassée sur chaque ligne de matériel serait pire que pas d'image du
          tout : `onError` remonte le refus, et l'écran cesse d'en demander partout —
          en le DISANT une fois, plutôt qu'en le laissant deviner.
        */}
        <img src={adresse} alt="" onError={onRefus} />
      </button>
      {ouvert && (
        <span className="qr-materiel__grand">
          <img src={adresse} alt={`Étiquette QR de ${libelle}`} />
          <span className="note">Scanner pour ouvrir la fiche de {libelle}</span>
        </span>
      )}
    </>
  );
}

/** Ce que chaque onglet retient du planning général. */
function filtrer(besoins: readonly BesoinDate[], onglet: Onglet): readonly BesoinDate[] {
  /*
    **Le transport voit ce qu'il transporte, et plus seulement ses camions.**

    *Demande de Chris, 03/09/2026 : « il faudrait rajouter les pastilles des tonnages ou des
    articles pour compléter l'information, avec également toutes les infos des heures,
    provenances etc., pour bien aiguiller le responsable ».*

    Les rotations disaient quel moyen mobiliser sans jamais dire ce qu'il y avait à charger : le
    tonnage vivait sous l'onglet Carrières, et encore, seulement s'il venait du groupe. Les deux
    natures entrent donc ici — **toutes** les livraisons, y compris celles qu'un fournisseur
    assure : savoir qu'il en arrive 20 t à 8 h change l'organisation du chantier, même quand
    aucun camion du groupe ne bouge (décision de Chris, 03/09/2026).

    Une livraison reste en **information** : elle ne s'affecte pas. C'est la rotation qui porte
    le camion et le chauffeur, et deux endroits pour la même réponse est ce qu'on a fermé le
    20/08 sur l'acheminement.
  */
  if (onglet === "transport") {
    return besoins.filter((b) => b.nature === "transport" || b.nature === "livraison");
  }
  if (onglet === "carrieres") {
    // Ce que la carrière doit produire : les matériaux du groupe, quels que soient le
    // camion et la main qui viendront les chercher. Un matériau externe ne la concerne
    // pas, même s'il se livre le même jour.
    return besoins.filter((b) => b.nature === "livraison" && b.duGroupe);
  }
  // L'exploitation : les hommes et les machines. Les rotations sont au transport, les
  // fournitures à personne — elles ne s'affectent pas, elles se livrent.
  return besoins.filter((b) => b.nature === "heures" && !b.estTransport);
}

type Regroupement = {
  readonly jour: string;
  readonly chantiers: readonly {
    readonly chantier: ChantierAuPlan;
    readonly besoins: readonly BesoinDate[];
  }[];
};

/** Les besoins rangés par jour, puis par chantier — la façon dont un responsable lit. */
function regrouper(
  besoins: readonly BesoinDate[],
  chantiers: readonly ChantierAuPlan[],
): readonly Regroupement[] {
  const parJour = new Map<string, Map<string, BesoinDate[]>>();
  for (const besoin of besoins) {
    const jour = parJour.get(besoin.date) ?? new Map<string, BesoinDate[]>();
    const liste = jour.get(besoin.chantierId) ?? [];
    liste.push(besoin);
    jour.set(besoin.chantierId, liste);
    parJour.set(besoin.date, jour);
  }

  const connus = new Map(chantiers.map((c) => [c.chantierId, c]));
  return [...parJour.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([jour, parChantier]) => ({
      jour,
      chantiers: [...parChantier.entries()]
        .map(([chantierId, liste]) => ({ chantier: connus.get(chantierId), besoins: liste }))
        .filter(
          (e): e is { chantier: ChantierAuPlan; besoins: BesoinDate[] } =>
            e.chantier !== undefined,
        )
        .sort((a, b) => a.chantier.apo.localeCompare(b.chantier.apo)),
    }));
}

export function TableauxDeBord({
  ongletInitial,
  peutAffecter,
  peutVoirLesAnomalies,
  cible,
  onOuvrirLaLigne,
}: {
  ongletInitial: Onglet;
  /**
   * Ce que le planning des ressources demande de montrer *(Chris, 17/09/2026)*.
   *
   * Arrive par `postMessage` depuis l'autre fenêtre, reçu par `App` qui bascule sur cet écran
   * avant de le passer ici. Un objet neuf à chaque clic, même sur la même case : c'est son
   * identité qui déclenche le repositionnement, sans quoi recliquer après avoir fait défiler la
   * page ne ramènerait rien à l'écran.
   */
  cible?: CibleDuPlanDeCharge | null;
  /**
   * Remonter d'une rangée du plan de charge à sa ligne de contre-étude *(Chris, 17/09/2026)*.
   *
   * Le second maillon de la chaîne. Il change de chantier, de version et d'écran — trois choses
   * que cet écran ne détient pas —, d'où le rappel plutôt qu'une navigation d'ici.
   */
  onOuvrirLaLigne?: (besoin: BesoinDate) => void;
  /** Les trois titulaires et l'administrateur. Les autres regardent (PRD §4.8). */
  peutAffecter: boolean;
  /**
   * L'administrateur et le responsable exploitation, eux seuls *(Chris, 16/09/2026)*.
   *
   * L'onglet est masqué aux autres, et ce masque n'est **qu'un confort** : le serveur rend 403
   * sur la route, comme partout dans ce produit. Un onglet caché n'a jamais fermé une API.
   */
  peutVoirLesAnomalies: boolean;
}): JSX.Element {
  const [onglet, setOnglet] = useState<Onglet>(ongletInitial);
  /**
   * Ce qu'on regarde sous Exploitation : le plan de charge, ou les absences.
   *
   * *Demande du métier, 16/08/2026 : « je préférerais ne pas avoir les tableaux les uns
   * sous les autres, mais sélectionner quand je clique sur exploitation soit plan de
   * charge soit absences ».*
   *
   * Deux vues qui se choisissent, et non deux blocs empilés : le plan de charge d'une
   * période de trois mois fait des centaines de lignes, et les absences se trouvaient
   * tout en bas — c'est-à-dire nulle part. C'est aussi la forme que prendra le droit
   * annoncé (« les membres voient le plan de charge mais pas les absences ») : une vue
   * qu'on n'offre pas, plutôt qu'un bloc à masquer au milieu d'une page.
   */
  const [vueExploitation, setVueExploitation] = useState<"charge" | "absences">("charge");
  /**
   * Laquelle des deux vues du transport (04/09/2026).
   *
   * L'acheminement par défaut : c'est le tableau qui existait, et celui que le responsable
   * ouvre chaque jour. Le matériel se regarde par périodes, quand on prépare une semaine.
   */
  const [vueTransport, setVueTransport] = useState<"acheminement" | "materiel">("acheminement");
  /** `null` = l'horizon par défaut du serveur, J-7 → J+80. Voir `Periode.tsx`. */
  const [periode, setPeriode] = useState<Bornes | null>(null);
  const [plan, setPlan] = useState<PlanGeneral | null>(null);
  const [salaries, setSalaries] = useState<readonly Salarie[]>([]);
  const [materiels, setMateriels] = useState<readonly Materiel[]>([]);
  /** Le préfixe de ce que les QR encodent, rendu avec le parc : voir `adresseQrMateriel`. */
  const [baseQr, setBaseQr] = useState("");
  /** Le référentiel des carrières : il donne la dénomination que le besoin ne porte pas. */
  const [carrieres, setCarrieres] = useState<readonly Carriere[]>([]);
  /** Les codes génériques : c'est leur type (`MO` / `MAT`) qui décide de la liste à proposer. */
  const [generiques, setGeneriques] = useState<readonly Generique[]>([]);
  /**
   * Les absences, par `salarié#jour`.
   *
   * *Demande du métier, 16/08/2026 : « attention, nous avons dit que tu devais prendre en
   * compte les congés et arrêts maladie ».*
   *
   * Chargées **à part** du planning général, et une panne de chargement n'est pas
   * affichée comme une erreur : si le référentiel maître ne répond pas, le tableau de
   * bord reste entièrement utilisable et seul le marquage « absent » manque. Les lier au
   * plan ferait dépendre l'écran entier de la disponibilité d'une autre application.
   */
  const [absences, setAbsences] = useState<ReadonlyMap<string, Absence>>(new Map());
  /**
   * Combien d'absences le référentiel n'impute à personne, sur la période affichée.
   *
   * *Signalé par le métier le 16/08/2026 : « j'ai affecté TARIS le 3/09 alors que je sais
   * qu'il est en arrêt et cela ne me le signale pas ».* Mesuré le jour même : le
   * référentiel ne rendait aucune absence pour cette personne. L'écran ne pouvait rien
   * dire — et c'est précisément le problème, parce que **ne rien dire se lit comme « il
   * n'y a rien »**.
   *
   * Ce compteur ne rattache rien : la correction se fait dans le référentiel maître. Il
   * rend seulement le trou visible, ce qui suffit à ne plus faire confiance à un tableau
   * d'absences vide.
   */
  const [absencesSansSalarie, setAbsencesSansSalarie] = useState(0);
  /**
   * Le référentiel a refusé les étiquettes QR.
   *
   * Une seule image en échec suffit à les retirer toutes : la cause est la même partout —
   * la clé du référentiel n'ouvre pas ce périmètre — et laisser une image cassée sur
   * chaque ligne de matériel serait pire que ne rien afficher. L'écran le **dit** une
   * fois, plutôt que de laisser chercher.
   */
  const [qrRefuse, setQrRefuse] = useState(false);
  /**
   * Le jour selon le SERVEUR.
   *
   * Il décide des semaines ouvertes par défaut et de la colonne marquée dans le tableau
   * des carrières. Le lire dans le navigateur le lirait dans le fuseau du poste — et un
   * poste réglé sur la veille ouvrirait les mauvaises semaines sans que rien ne le dise.
   */
  const [aujourdhui, setAujourdhui] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState<string | null>(null);
  /**
   * L'affaire regardée, ou `""` pour toutes.
   *
   * *Demande de Chris, 15/08/2026 : « il faut que les responsables puissent filtrer le
   * tableau de bord par APO ».* À vingt chantiers, un tableau qui déroule tout fait
   * chercher trois rotations dans trente lignes.
   */
  const [affaire, setAffaire] = useState("");

  /**
   * L'horizon par défaut, qui n'est pas le même partout.
   *
   * *« Par défaut tu présentes de J-7 à la dernière date qui existe dans un chantier en
   * cours » — pour les carrières (métier, 16/08/2026).* Une carrière commande sa
   * production sur tout ce qui est engagé, pas sur un trimestre : ce qu'elle doit sortir
   * en février se prépare en novembre. Le transport et l'exploitation, eux, gardent
   * J-7 → J+80 : ils affectent des personnes et des machines, et personne n'affecte
   * quelqu'un à huit mois.
   *
   * Mémorisé sur le seul fait d'être ou non sur les carrières : sans cela, passer de
   * transport à exploitation rechargerait le plan pour rien.
   */
  const versCarrieres = onglet === "carrieres";
  const defaut = useMemo(
    () => (versCarrieres ? { fin: "derniere" } : undefined),
    [versCarrieres],
  );

  const recharger = useCallback((): void => {
    /*
      L'onglet des anomalies ne lit pas le plan : il a sa propre route, qui le calcule au serveur
      avec les comptes et les absences. Charger le plan ici lui ferait payer une lecture complète
      du planning général sans jamais l'afficher — et sur une période de trois mois, c'est la
      lecture la plus chère du produit.
    */
    if (onglet === "anomalies") {
      setChargement(false);
      return;
    }
    setChargement(true);
    chargerPlan(periode ?? defaut)
      .then((r) => {
        setPlan(r.plan);
        setAujourdhui(r.aujourdhui);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setChargement(false);
      });
  }, [periode, defaut, onglet]);

  useEffect(recharger, [recharger]);

  /* ---------------------------------------------------------------------------
   * Arriver du planning des ressources (Chris, 17/09/2026)
   * ------------------------------------------------------------------------- */

  /** Les rangées à surligner, par ancre — jour compris. Vide tant que rien n'a été cliqué. */
  const ancresCiblees = useMemo(
    () =>
      cible === undefined || cible === null
        ? (new Set<string>() as ReadonlySet<string>)
        : ancresDeLaCible(cible),
    [cible],
  );

  /**
   * Se poser sur l'onglet, la vue et l'affaire que la case cliquée désigne.
   *
   * **La vue est forcée à celle qui porte le plan de charge.** On peut très bien avoir laissé
   * l'écran sur « Absences » ou sur « Matériel transport » avant d'aller regarder le planning :
   * y revenir sans rétablir la bonne vue afficherait un écran qui ne contient pas la rangée
   * demandée, et le défilement n'aurait rien à atteindre.
   */
  useEffect(() => {
    if (cible === undefined || cible === null) return;
    setOnglet(cible.vue);
    if (cible.vue === "transport") setVueTransport("acheminement");
    else setVueExploitation("charge");
    setAffaire(cible.chantierId ?? "");
  }, [cible]);

  /**
   * Élargir la période quand le jour visé n'y est pas.
   *
   * Les deux fenêtres s'ouvrent sur le même horizon, mais le planning des ressources a son
   * propre sélecteur de dates : on y regarde volontiers trois semaines de plus. Sans cet
   * élargissement, cliquer sur une case hors période ramènerait sur un plan de charge qui ne
   * contient pas ce jour-là — et l'écran se lirait « il n'y a rien », ce qui serait faux.
   *
   * **Une fois par cible**, gardée par une référence : le serveur rend les bornes qu'il a
   * réellement retenues, et rien ne garantit qu'elles couvrent exactement ce qui a été demandé.
   * Sans ce verrou, un jour que le serveur refuse d'inclure relancerait le chargement sans fin.
   */
  const cibleElargie = useRef<CibleDuPlanDeCharge | null>(null);
  useEffect(() => {
    if (cible === undefined || cible === null || plan === null) return;
    if (cibleElargie.current === cible) return;
    if (cible.jour >= plan.debut && cible.jour <= plan.fin) return;
    cibleElargie.current = cible;
    setPeriode({
      debut: cible.jour < plan.debut ? cible.jour : plan.debut,
      fin: cible.jour > plan.fin ? cible.jour : plan.fin,
    });
  }, [cible, plan]);

  /**
   * Amener la rangée sous les yeux, dès qu'elle est rendue.
   *
   * L'effet se rejoue à chaque étape du chemin — l'onglet change, l'affaire change, le plan se
   * recharge — parce que la rangée n'existe qu'au bout. La référence arrête le défilement dès
   * qu'il a réussi : sans elle, chaque rechargement du plan ramènerait l'écran sur cette rangée
   * alors qu'on est peut-être déjà reparti travailler ailleurs.
   */
  const cibleAtteinte = useRef<CibleDuPlanDeCharge | null>(null);
  useEffect(() => {
    if (cible === undefined || cible === null || cibleAtteinte.current === cible) return;
    const premiere = cible.occupations[0];
    if (premiere === undefined) return;
    if (amenerSousLesYeux(ancreDuPlan(cible.jour, cleDuBesoin(premiere)))) {
      cibleAtteinte.current = cible;
    }
  }, [cible, plan, onglet, affaire, chargement]);

  // Les deux référentiels réels, chargés une fois : ils ne bougent pas pendant qu'on
  // affecte, et les recharger à chaque liste déroulante ferait un appel par ligne.
  // Une panne du référentiel n'empêche pas de LIRE le tableau de bord : seules les
  // listes de choix en dépendent, et le message se verra à la première affectation.
  useEffect(() => {
    chargerSalaries()
      .then((r) => {
        setSalaries(r.salaries);
      })
      .catch(() => undefined);
    chargerMateriels()
      .then((r) => {
        setMateriels(r.materiels);
        setBaseQr(r.baseQr);
      })
      .catch(() => undefined);
    chargerCarrieres()
      .then((r) => {
        setCarrieres(r.carrieres);
      })
      .catch(() => undefined);
    chargerGeneriques()
      .then((r) => {
        setGeneriques(r.generiques);
      })
      .catch(() => undefined);
  }, []);

  /**
   * Les absences de la période affichée.
   *
   * Rechargées quand la période change, et **jamais fatales** : `catch` vide la carte
   * plutôt que d'afficher une erreur. Un référentiel muet ne doit pas passer pour une
   * panne de la planification — c'est la même règle que pour les listes de salariés et
   * de matériels.
   */
  useEffect(() => {
    if (plan === null) return;
    let abandonne = false;
    chargerIndisponibilites(plan.debut, plan.fin)
      .then((r) => {
        if (abandonne) return;
        setAbsences(
          new Map(
            r.indisponibilites.map((i) => [
              `${i.salarieId}#${i.date}`,
              { nature: i.nature, valide: i.valide },
            ]),
          ),
        );
        setAbsencesSansSalarie(r.sansSalarie);
      })
      .catch(() => {
        if (!abandonne) {
          setAbsences(new Map());
          // Une panne du référentiel n'est pas un trou de rattachement : remettre le
          // compteur à zéro évite d'afficher un avertissement dont la cause a disparu.
          setAbsencesSansSalarie(0);
        }
      });
    return () => {
      abandonne = true;
    };
  }, [plan]);

  /**
   * Les besoins de l'onglet, toutes affaires comprises.
   *
   * Séparés du filtre par affaire parce que le menu déroulant a besoin des **autres** affaires :
   * c'est là qu'il annonce ce qu'il leur reste à affecter. Les calculer après le filtre ferait
   * un menu qui ne sait rien dire de ce qu'on ne regarde pas — donc précisément de ce qu'on
   * risque d'oublier.
   */
  const besoinsDeLOnglet = useMemo(
    () => filtrer(plan?.besoins ?? [], onglet),
    [plan, onglet],
  );

  const retenus = useMemo(
    () =>
      // Le filtre par affaire porte sur ce qu'on AFFICHE, pas sur ce qu'on charge : le
      // signalement de conflit doit continuer à voir les autres chantiers, sinon regarder
      // une affaire ferait disparaître la raison même du conflit.
      affaire === "" ? besoinsDeLOnglet : besoinsDeLOnglet.filter((b) => b.chantierId === affaire),
    [besoinsDeLOnglet, affaire],
  );

  /**
   * Ce qu'il reste à affecter, par affaire — la couleur du menu déroulant.
   *
   * Calculée sur l'onglet et non sur le produit entier (voir `couvertureParAffaire`) : sous
   * Transport, ce sont les rotations qui décident ; sous Exploitation, les hommes et les
   * machines. Les carrières n'affectent rien, leur menu reste donc neutre.
   */
  const couverture = useMemo(
    () => (onglet === "carrieres" ? new Map() : couvertureParAffaire(besoinsDeLOnglet)),
    [besoinsDeLOnglet, onglet],
  );

  /**
   * Le total sur l'option « toutes », et la couleur du menu quand aucune affaire n'est choisie.
   *
   * **Sans lui, l'alerte n'apparaîtrait jamais** : l'écran s'ouvre sur « toutes les affaires »,
   * et un menu qui ne rougit qu'après avoir choisi un chantier n'avertit personne — il faudrait
   * déjà savoir où regarder.
   */
  const chargeGlobale = useMemo(() => {
    let affairesIncompletes = 0;
    let besoins = 0;
    for (const compte of couverture.values()) {
      besoins += compte.besoins;
      if (compte.aAffecter > 0) affairesIncompletes += 1;
    }
    return { affairesIncompletes, besoins };
  }, [couverture]);

  const aAffecterEnTout =
    chargeGlobale.affairesIncompletes === 0
      ? chargeGlobale.besoins === 0
        ? ""
        : " · tout est affecté"
      : chargeGlobale.affairesIncompletes === 1
        ? " · 1 affaire à compléter"
        : ` · ${String(chargeGlobale.affairesIncompletes)} affaires à compléter`;

  /** L'état que le menu refermé doit montrer : celui de l'affaire choisie, ou celui de toutes. */
  const etatDuMenu =
    affaire === ""
      ? chargeGlobale.affairesIncompletes > 0
        ? "manque"
        : chargeGlobale.besoins === 0
          ? "neutre"
          : "complet"
      : direLaCharge(couverture.get(affaire)).etat;

  const groupes = useMemo(
    () => regrouper(retenus, plan?.chantiers ?? []),
    [retenus, plan],
  );

  /**
   * Combien de ressources distinctes sont en conflit sur la période.
   *
   * Compté sur **tous** les besoins et non sur ceux qu'on affiche : le conflit existe même
   * quand on regarde une autre affaire, et il concerne les trois natures. Compté par
   * ressource et par jour, pas par ligne — la même pelle signalée sur quatre lignes du
   * même jour est un problème, pas quatre.
   */
  const nombreDeConflits = useMemo(() => {
    const vus = new Set<string>();
    for (const besoin of plan?.besoins ?? []) {
      for (const affectation of besoin.affectations) {
        if (affectation.conflit.length > 0) vus.add(`${affectation.ressourceId}#${besoin.date}`);
      }
    }
    return vus.size;
  }, [plan]);

  /**
   * Ce que la table d'association a fait, ou n'a pas pu faire, en même temps que la personne.
   *
   * *Le matériel posé se voit tout seul — il apparaît sur sa ligne au rechargement. **Ce qui ne
   * se voit pas, c'est ce qui n'a PAS été posé** : sans un mot, une association qui ne trouve
   * aucune ligne ce jour-là se lit comme une fonction en panne, et on irait chercher le défaut
   * dans l'écran d'administration où tout est pourtant juste.*
   *
   * **La note vit SUR LA LIGNE du geste, et non en haut de l'écran.** Premier essai réel de
   * Chris, le 10/09/2026 : *« je sélectionne CAPDEGEL, en dessous il y a un VUL, rien ne se
   * passe »* — la phrase était bien produite, mais affichée au-dessus des tableaux, à plusieurs
   * écrans de défilement de la ligne qu'il regardait. Un message qu'il faut aller chercher n'est
   * pas un message.
   */
  const [noteAssociee, setNoteAssociee] = useState<{
    readonly cle: string;
    readonly texte: string;
  } | null>(null);

  const agir = (promesse: Promise<unknown>, cle: string): void => {
    setOccupe(cle);
    setErreur(null);
    promesse
      .then(recharger)
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  /**
   * Les ressources proposables pour un besoin.
   *
   * **Filtrées sur le code générique du besoin**, exactement comme l'équipe du chantier
   * l'est sur le code MO depuis la 017 : proposer les soixante machines du parc pour un
   * besoin de niveleuse rendrait la liste inutilisable. Si le référentiel ne rattache
   * personne à ce code, la liste s'ouvre en entier plutôt que de rester vide — mieux vaut
   * chercher que ne rien pouvoir affecter.
   *
   * ## Ce qui décide, c'est le RÉFÉRENTIEL des génériques — pas la nature du besoin
   *
   * *Signalé par le métier le 16/08/2026 : « sur le tableau de bord transport il y a un
   * souci avec le référentiel du MOICHAUF, il affiche le matériel au lieu du référentiel
   * des salariés ».*
   *
   * La règle était « une rotation de transport, donc du matériel ». Elle est fausse : une
   * ligne de transport porte un camion **ou un chauffeur**, et `_MOICHAUF` est un code de
   * main-d'œuvre. Le tableau proposait donc le parc entier pour affecter un homme.
   *
   * Le type vient du **référentiel des ressources génériques** (`MO` ou `MAT`), celui que
   * l'administrateur tient à l'écran. Ni le préfixe du code — un code d'ERP écrit en dur
   * serait un fork qui commence —, ni la nature du besoin ne peuvent le dire à sa place.
   */
  const typeDuCode = useMemo(
    () => new Map(generiques.map((g) => [g.code, g.type])),
    [generiques],
  );

  /**
   * La famille de chaque code générique, et comment elle se nomme.
   *
   * Les deux viennent du référentiel que l'administrateur tient (`famille_code`,
   * `famille_libelle`) : c'est ce qui permet de proposer « les niveleuses » quand aucune machine
   * ne porte le code exact du besoin, sans jamais découper un code d'ERP à la main.
   */
  const familleDuCode = useMemo(
    () =>
      new Map(
        generiques
          .filter((g) => g.familleCode !== null && g.familleCode !== "")
          .map((g) => [g.code, g.familleCode ?? ""]),
      ),
    [generiques],
  );
  /** La dénomination d'une carrière, par son code — pour dire d'où sort le matériau. */
  const nomDeLaCarriere = useMemo(
    () => new Map(carrieres.map((c) => [c.code, c.denomination])),
    [carrieres],
  );

  const libelleDeLaFamille = useMemo(
    () =>
      new Map(
        generiques
          .filter((g) => g.familleCode !== null && g.familleLibelle !== null)
          .map((g) => [g.familleCode ?? "", g.familleLibelle ?? ""]),
      ),
    [generiques],
  );

  /**
   * La liste jour par jour est-elle à l'écran ?
   *
   * Les carrières ont leur propre présentation, et l'exploitation la remplace par le
   * tableau des absences quand on choisit cette vue. Un seul prédicat, parce que trois
   * conditions recopiées finissent par ne plus dire la même chose — et l'écart se verrait
   * comme deux tableaux affichés l'un sur l'autre.
   */
  const planDeCharge =
    (onglet === "transport" && vueTransport === "acheminement") ||
    (onglet === "exploitation" && vueExploitation === "charge");

  /** L'absence d'une ressource un jour donné, ou `undefined`. Vide pour une machine. */
  const absenceDe = (ressourceId: string, date: string): Absence | undefined =>
    absences.get(`${ressourceId}#${date}`);

  /**
   * Combien de personnes affectées sont absentes sur la période.
   *
   * Compté sur **tout** le plan et non sur l'onglet ni sur l'affaire regardée : c'est une
   * alerte, et une alerte qui disparaît quand on filtre n'alerte personne. Compté par
   * personne et par jour, comme les conflits — la même personne signalée sur trois lignes
   * du même jour est un problème, pas trois.
   */
  const nombreDAbsents = useMemo(() => {
    const vus = new Set<string>();
    for (const besoin of plan?.besoins ?? []) {
      for (const affectation of besoin.affectations) {
        if (absences.has(`${affectation.ressourceId}#${besoin.date}`)) {
          vus.add(`${affectation.ressourceId}#${besoin.date}`);
        }
      }
    }
    return vus.size;
  }, [plan, absences]);

  const proposables = (besoin: BesoinDate): Proposition<CandidatAffectable> => {
    const desMateriels = (
      liste: readonly Materiel[],
    ): { id: string; libelle: string; nature: "materiel"; absent: boolean }[] =>
      liste.map((m) => ({
        id: m.id,
        libelle: designerMateriel(m),
        nature: "materiel" as const,
        // Les indisponibilités du référentiel maître ne portent que sur les personnes.
        // Le module SAV (indisponibilités matériel) est reporté en V2.
        absent: false,
      }));

    const desSalaries = (
      liste: readonly Salarie[],
    ): { id: string; libelle: string; nature: "salarie"; absent: boolean }[] =>
      liste.map((s) => {
        const absence = absences.get(`${s.id}#${besoin.date}`);
        return {
          id: s.id,
          libelle:
            `${s.nom} ${s.prenom}${s.poste === null ? "" : ` · ${s.poste}`}` +
            // La nature ET la validation, toujours ensemble : « congé » sans « validé »
            // laisse croire que c'est acquis, et « à confirmer » sans la nature ne dit
            // pas s'il faut chercher un remplaçant ou trancher une demande.
            (absence === undefined ? "" : ` · ${direLAbsence(absence).toUpperCase()}`),
          nature: "salarie" as const,
          absent: absence !== undefined,
        };
      });

    const type = typeDuCode.get(besoin.code);
    const salariesDuCode = salaries.filter((s) => s.codeMo === besoin.code);
    const materielsDuCode = materiels.filter((m) => m.generique === besoin.code);

    /*
      **La famille du code du besoin**, quand le référentiel la donne. C'est elle qui remplace
      le déversement du parc entier : une niveleuse chiffrée `04-03-18-100` se trouve au parc
      sous `04-03-18-200`, et les deux partagent la famille `04-03-18` — donnée par les deux
      référentiels, jamais découpée à la main dans le code.
    */
    const famille = familleDuCode.get(besoin.code) ?? null;
    const materielsDeLaFamille =
      famille === null
        ? []
        : materiels.filter((m) => familleDeLaMachine(m, (c) => familleDuCode.get(c) ?? null) === famille);

    /*
      **Le reste du référentiel, ouvert sous la liste filtrée** (Chris, 10/09/2026). Les salariés
      que le code du besoin ne rattache pas — y compris ceux dont le `code_mo` est vide, qui ne
      figuraient dans aucune liste. Le complément est exact, calculé sur le même critère que
      `salariesDuCode` : personne n'apparaît deux fois, personne ne manque.
    */
    const autresSalaries = () => desSalaries(salaries.filter((s) => s.codeMo !== besoin.code));

    /*
      La main d'œuvre n'a pas de famille au référentiel : les codes `_MOI…` ne portent pas de
      `famille_code`. Son repli reste donc le référentiel entier — mais **annoncé**, ce qui
      était tout le défaut : une liste de cent trente-six noms sans un mot se lit comme un
      filtre en panne.

      Et depuis le 10/09/2026, la liste filtrée n'est plus une impasse : un salarié rattaché au
      mauvais code de main d'œuvre se retrouve sous « Autres ressources » sans qu'il faille aller
      corriger le référentiel maître, où la planification n'écrit rien.
    */
    if (type === "MO") {
      return ouvrirLeReferentiel(
        choisirCandidats({
          code: besoin.code,
          duCode: desSalaries(salariesDuCode),
          deLaFamille: [],
          tous: desSalaries(salaries),
          familleLibelle: null,
        }),
        autresSalaries(),
      );
    }
    if (type === "MAT") {
      return choisirCandidats({
        code: besoin.code,
        duCode: desMateriels(materielsDuCode),
        deLaFamille: desMateriels(materielsDeLaFamille),
        tous: desMateriels(materiels),
        familleLibelle: libelleDeLaFamille.get(famille ?? "") ?? null,
      });
    }

    /*
      Le code n'est pas au référentiel des génériques — une ligne ajoutée à la main, ou un
      code que l'administration n'a pas encore saisi. On demande alors aux deux
      référentiels réels qui le connaît : c'est encore une donnée, pas une supposition.
    */
    if (salariesDuCode.length > 0) {
      return ouvrirLeReferentiel(
        choisirCandidats({
          code: besoin.code,
          duCode: desSalaries(salariesDuCode),
          deLaFamille: [],
          tous: desSalaries(salaries),
          familleLibelle: null,
        }),
        autresSalaries(),
      );
    }
    // Personne ne connaît ce code : `estMateriel` reste le dernier indice — il vient du
    // type de ressource du DEPO lui-même, et il est vrai pour une machine en heures.
    return besoin.estMateriel
      ? choisirCandidats({
          code: besoin.code,
          duCode: desMateriels(materielsDuCode),
          deLaFamille: desMateriels(materielsDeLaFamille),
          tous: desMateriels(materiels),
          familleLibelle: libelleDeLaFamille.get(famille ?? "") ?? null,
        })
      : choisirCandidats({
          code: besoin.code,
          duCode: [],
          deLaFamille: [],
          tous: desSalaries(salaries),
          familleLibelle: null,
        });
  };

  const barreDesOnglets = (
    <nav className="menu menu--onglets">
      {(Object.keys(TITRES) as Onglet[])
        .filter((cle) => cle !== "anomalies" || peutVoirLesAnomalies)
        .map((cle) => (
          <button
            key={cle}
            type="button"
            className={onglet === cle ? "actif" : undefined}
            onClick={() => {
              setOnglet(cle);
            }}
          >
            {TITRES[cle]}
          </button>
        ))}
    </nav>
  );

  /*
    **L'onglet des anomalies sort ici** (Chris, 16/09/2026).

    Il ne partage rien avec les trois autres : ni la période — la sienne est dans son écran —, ni
    le filtre par affaire, qui n'a pas de sens sur un tableau trié par échéance, ni la grille
    d'affectation. Le rendre sous le bandeau commun aurait posé au-dessus de lui trois commandes
    dont aucune ne l'atteint, et c'est le meilleur moyen de faire chercher pourquoi elles ne font
    rien. La barre d'onglets est partagée, elle : c'est le seul élément commun.
  */
  if (onglet === "anomalies") {
    return (
      <section className="plan">
        {barreDesOnglets}
        <AnomaliesAffectation />
      </section>
    );
  }

  /*
    **La facturation prévisionnelle sort ici aussi**, et pour la raison qui a fait sortir les
    anomalies : elle ne partage rien avec les trois onglets d'affectation. Sa période se compte
    en MOIS et non en jours, elle ne filtre pas par affaire, et elle n'affecte personne. La
    servir sous le bandeau commun aurait posé au-dessus d'elle trois commandes dont aucune ne
    l'atteint.
  */
  if (onglet === "facturation") {
    return (
      <section className="plan">
        {barreDesOnglets}
        <FacturationPrevisionnelle />
      </section>
    );
  }

  return (
    <section className="plan">
      {barreDesOnglets}

      {/*
        Le second niveau, sous Exploitation seulement (métier, 16/08/2026). Il n'apparaît
        pas ailleurs : un jeu d'onglets toujours visible mais actif une fois sur trois
        ferait chercher ce qu'il commande quand on est sur Transport.
      */}
      {onglet === "exploitation" && (
        <nav className="menu menu--onglets menu--second">
          <button
            type="button"
            className={vueExploitation === "charge" ? "actif" : undefined}
            onClick={() => {
              setVueExploitation("charge");
            }}
          >
            Plan de charge
          </button>
          <button
            type="button"
            className={vueExploitation === "absences" ? "actif" : undefined}
            onClick={() => {
              setVueExploitation("absences");
            }}
          >
            Absences à gérer
            {nombreDAbsents > 0 && <span className="menu__compteur">{nombreDAbsents}</span>}
          </button>
        </nav>
      )}

      {/*
        **Le transport a deux tableaux depuis le 04/09/2026**, et chacun porte son nom.

        *« Le tableau actuel concerne le transport des articles et matériaux, tu peux appeler ce
        tableau “Acheminement articles & matériaux” ; il faudrait créer un deuxième tableau de
        bord “Amené et repli de matériel”. »*

        Deux tableaux sans titre dans un même onglet ne se distinguent plus — et nommer le
        premier, c'est surtout dire ce qu'il ne contient PAS : les machines à monter et à
        ramener sont l'affaire du second, qui ne lit pas les mêmes objets du planning.
      */}
      {onglet === "transport" && (
        <nav className="menu menu--onglets menu--second">
          <button
            type="button"
            className={vueTransport === "acheminement" ? "actif" : undefined}
            onClick={() => {
              setVueTransport("acheminement");
            }}
          >
            Acheminement articles &amp; matériaux
          </button>
          <button
            type="button"
            className={vueTransport === "materiel" ? "actif" : undefined}
            onClick={() => {
              setVueTransport("materiel");
            }}
          >
            Amené et repli de matériel
          </button>
        </nav>
      )}

      {onglet === "transport" && vueTransport === "materiel" && (
        <MouvementsMateriel modifiable={peutAffecter} />
      )}

      {onglet === "transport" && vueTransport === "acheminement" && (
        <h3 className="plan__nom-tableau">Acheminement articles &amp; matériaux</h3>
      )}

      <p className="etat">
        {EXPLICATIONS[onglet]}{" "}
        <label className="plan__filtre">
          Affaire
          {/*
            **Le menu dit ce qu'il reste à affecter, affaire par affaire** (Chris, 03/09/2026).

            Deux porteurs pour une seule information, et ce n'est pas de la redondance : la
            couleur se voit sans lire, le texte se lit quand la couleur ne s'affiche pas — une
            `<option>` colorée n'est pas rendue partout, et le responsable ne doit pas dépendre
            de ça pour savoir qu'il a du travail. La classe est posée sur le `select` lui-même :
            sans elle, l'alerte disparaîtrait dès que la liste est refermée, c'est-à-dire tout
            le temps.
          */}
          <select
            className={`plan__affaire plan__affaire--${etatDuMenu}`}
            value={affaire}
            onChange={(e) => {
              setAffaire(e.target.value);
            }}
          >
            <option value="">
              toutes ({(plan?.chantiers ?? []).length}){aAffecterEnTout}
            </option>
            {(plan?.chantiers ?? []).map((c) => {
              const charge = direLaCharge(couverture.get(c.chantierId));
              return (
                <option
                  key={c.chantierId}
                  value={c.chantierId}
                  className={`plan__affaire--${charge.etat}`}
                >
                  {c.apo} — {c.nom}
                  {charge.suffixe}
                </option>
              );
            })}
          </select>
        </label>
        {/*
          **Le planning des ressources s'ouvre à CÔTÉ, pas par-dessus.**

          *Demande de Chris, 03/09/2026 : « il ouvre une fenêtre supplémentaire simultanément à
          sa vue du plan de charge pour visualiser les ressources main d'œuvre et matériel ».*
          Une boîte modale aurait recouvert précisément ce qu'il faut garder sous les yeux — on
          affecte dans un écran en regardant l'autre.

          La fenêtre porte un nom fixe : rechanger d'affaire et recliquer rafraîchit la même
          fenêtre au lieu d'en empiler une troisième. La période et l'affaire voyagent dans
          l'adresse, pour que les deux vues s'ouvrent sur le même horizon.

          Sous Exploitation seulement, parce que c'est là qu'il a été demandé. Le responsable
          transport affecte lui aussi des ressources réelles : le jour où il le demandera, le
          bouton est le même.
        */}
        {planDeCharge && plan !== null && " "}
        {planDeCharge && plan !== null && (
          <button
            type="button"
            className="lien"
            onClick={() => {
              const vue = onglet === "transport" ? "transport" : "exploitation";
              window.open(
                adresseDuPlanning({
                  debut: plan.debut,
                  fin: plan.fin,
                  chantierId: affaire,
                  vue,
                }),
                NOM_FENETRE(vue),
                // Une vraie fenêtre et non un onglet : un onglet ne se regarde pas en même
                // temps que celui qu'on quitte, ce qui est tout l'objet de la demande.
                "popup=yes,width=1500,height=900",
              );
            }}
          >
            {onglet === "transport"
              ? "Ouvrir le planning des ressources transport"
              : "Ouvrir le planning des ressources"}
          </button>
        )}
      </p>

      <PanneauPeriode
        affichee={plan === null ? null : { debut: plan.debut, fin: plan.fin }}
        chargement={chargement}
        onBorner={setPeriode}
        onDefaut={() => {
          setPeriode(null);
        }}
      />

      {/*
        Le compteur de conflits, en tête et sur TOUTE la période — pas seulement sur
        l'affaire regardée. Un décalage de chantier fabrique des doubles réservations sans
        qu'aucune affectation ne soit touchée : aucun contrôle à l'écriture ne peut les
        empêcher, seul l'affichage les voit. Les compter ici évite d'avoir à dérouler
        la période pour découvrir qu'il y en a un.
      */}
      {nombreDeConflits > 0 && (
        <p className="alerte">
          {nombreDeConflits === 1
            ? "1 ressource est affectée sur deux chantiers le même jour"
            : `${String(nombreDeConflits)} ressources sont affectées sur deux chantiers le même jour`}{" "}
          sur la période. Elles sont signalées en rouge dans la colonne « Affecté » — rien
          n'est bloqué, c'est à vous de trancher.
        </p>
      )}

      {/*
        Le compteur d'absences, à côté de celui des conflits et pour la même raison : il
        faut le voir sans dérouler. Compté sur tout le plan — une alerte qui disparaît
        quand on filtre par affaire n'alerte personne.
      */}
      {nombreDAbsents > 0 && (
        <p className="alerte">
          {nombreDAbsents === 1
            ? "1 personne affectée est absente"
            : `${String(nombreDAbsents)} affectations tombent un jour d'absence`}{" "}
          sur la période.{" "}
          {/*
            Le compteur MÈNE à la vue, il ne se contente pas de la nommer : depuis
            l'onglet Transport, « c'est sous Exploitation » demande deux clics et de
            savoir lequel.
          */}
          <button
            type="button"
            className="lien"
            onClick={() => {
              setOnglet("exploitation");
              setVueExploitation("absences");
            }}
          >
            Voir les absences à gérer
          </button>
        </p>
      )}

      {/*
        **Le trou de rattachement, dit à voix haute.**

        *« J'ai affecté TARIS le 3/09 alors que je sais qu'il est en arrêt et cela ne me le
        signale pas » (métier, 16/08/2026).* Le référentiel ne rendait aucune absence pour
        cette personne : rien n'était cassé ici, l'écran n'avait rien reçu. Sans ce
        message, un tableau d'absences vide se lit « tout le monde est là » — et c'est le
        pire mode de panne d'un écran d'alerte, puisque rien n'y paraît faux.

        Il n'y a rien à corriger ici : le rattachement d'une absence à une fiche salarié se
        fait dans le référentiel maître. L'écran dit ce qu'il ne sait pas, il ne le devine
        pas.
      */}
      {absencesSansSalarie > 0 && (
        <p className="alerte">
          {absencesSansSalarie === 1
            ? "1 absence du référentiel n'est rattachée à aucune fiche salarié"
            : `${String(absencesSansSalarie)} absences du référentiel ne sont rattachées à aucune fiche salarié`}{" "}
          sur cette période : elles ne peuvent être ni affichées ni signalées, et les
          personnes concernées apparaissent ici comme <strong>disponibles</strong>. À
          corriger dans le référentiel des salariés, pas dans la planification.
        </p>
      )}

      {/*
        Le refus des étiquettes est dit UNE fois, en clair, et n'empêche rien d'autre :
        c'est une commodité de consultation, pas une donnée de planification.
      */}
      {qrRefuse && (
        <p className="etat">
          Les étiquettes QR des machines ne s'affichent pas : le référentiel les a refusées
          (périmètre <code>materiels_qrcode</code> manquant sur la clé d'accès ?). Le reste
          du tableau de bord n'est pas concerné.
        </p>
      )}

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {chargement && <p className="etat">Chargement…</p>}

      {/*
        **Les absences sont une VUE, pas un bloc sous le plan de charge** (métier,
        16/08/2026). Sur trois mois, le plan de charge fait des centaines de lignes, et un
        tableau posé dessous ne se trouve nulle part.

        C'est aussi la forme que prendra le droit annoncé — *« les membres ont accès au
        plan de charge mais pas aux absences »* : une vue qu'on n'offre pas, plutôt qu'un
        bloc à masquer au milieu d'une page.
      */}
      {onglet === "exploitation" && vueExploitation === "absences" && !chargement && (
        <section className="plan__bloc">
          <p className="note">
            Les personnes affectées qui sont absentes ce jour-là. Ce tableau ne se
            déclenche pas : il croise le plan et le référentiel{" "}
            <strong>à chaque affichage</strong>, donc il ne peut pas être en retard sur les
            données. Rien n'est retiré — défaire l'affectation ferait croire le besoin
            couvert.
          </p>
          <AbsencesAGerer
            besoins={plan?.besoins ?? []}
            chantiers={plan?.chantiers ?? []}
            absences={absences}
          />
        </section>
      )}

      {/*
        **Les carrières ont leur propre présentation**, et non la liste jour par jour des
        deux autres onglets (métier, 16/08/2026). Ce n'est pas une préférence de mise en
        page : le transport et l'exploitation AFFECTENT une ressource à un besoin — donc
        une ligne par besoin, avec le geste au bout. Le responsable carrières, lui, ne
        s'affecte rien : il lit un plan de production, et ce qu'il cherche est un volume
        par article et par jour, tous chantiers confondus. Deux questions différentes,
        deux tableaux.
      */}
      {onglet === "carrieres" && !chargement && (
        <TableauCarrieres
          besoins={retenus}
          carrieres={carrieres}
          chantiers={plan?.chantiers ?? []}
          aujourdhui={aujourdhui}
        />
      )}

      {planDeCharge && !chargement && groupes.length === 0 && (
        <p className="etat">
          Rien de planifié sur cette période pour {TITRES[onglet].toLowerCase()}.
        </p>
      )}

      {planDeCharge && groupes.map((jour) => (
        <article key={jour.jour} className="plan__jour-bloc">
          <h3>{formaterDateLongue(jour.jour)}</h3>
          {jour.chantiers.map(({ chantier, besoins }) => (
            <div key={chantier.chantierId} className="plan__chantier-bloc">
              <h4>
                {chantier.apo} — {chantier.nom}
                {chantier.ville !== null && <span className="note"> · {chantier.ville}</span>}
                {chantier.dateDebutStatut === "hypothese" && (
                  <span className="plan__hypothese"> hypothèse</span>
                )}
              </h4>
              {/*
                **La classe dit le jeu de colonnes, parce qu'il y en a DEUX.** Le transport porte
                six colonnes, l'exploitation cinq (pas de trajet). Laisser le navigateur
                redistribuer les mêmes pourcentages sur cinq colonnes gonflerait la colonne des
                heures de l'exploitation, qui n'affiche que « 7 h » — un réglage demandé sur un
                tableau n'a pas à déformer l'autre.
              */}
              <table className={onglet === "transport" ? "plan__tableau--transport" : undefined}>
                {/*
                  **Les largeurs sont posées ici, et non laissées au contenu** — *demande de
                  Chris, 16/09/2026, sur l'acheminement : « la colonne heures/quantité doit être
                  élargie […] la colonne trajet peut être réduite de moitié avec un retour à la
                  ligne des libellés ».*

                  En largeur automatique, c'est le texte le plus long qui décide : le trajet
                  (« par nos soins · carrière ESCALANS · Escalans → Maillas · 32,5 km ») tient
                  sur une ligne en s'octroyant le quart du tableau, et comprime d'autant la
                  colonne des heures — celle qui porte la répartition matin/après-midi, le
                  calcul des tours et l'écart. La demande inverse le partage : le trajet passe
                  à la ligne, les heures respirent.

                  **Huitième fois que ce tableau se règle par un `colgroup`** : `table-layout:
                  fixed` ne regarde QUE ces largeurs-là — une largeur posée sur le `th` est
                  ignorée, et c'est ce qui a fait croire sept fois que le réglage n'avait pas
                  pris.
                */}
                <colgroup>
                  <col className="plan__col--poste" />
                  <col className="plan__col--ressource" />
                  <col className="plan__col--heures" />
                  {onglet === "transport" && <col className="plan__col--trajet" />}
                  <col className="plan__col--affecte" />
                  <col className="plan__col--ajouter" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th>Ressource</th>
                    <th className="nombre">{onglet === "transport" ? "Heures / quantité" : "Heures"}</th>
                    {/*
                      **La provenance, sous l'onglet Transport seulement.**

                      *Demande de Chris, 03/09/2026 : « avec également toutes les infos des
                      heures, provenances etc., pour bien aiguiller le responsable ».* Elle ne
                      dit rien à l'exploitation, qui affecte des hommes et des machines à un
                      travail : une colonne vide sur les trois quarts des lignes serait du bruit.
                    */}
                    {/* « Provenance » depuis le 03/09, mais la colonne dit le trajet entier
                        depuis le 09/09 : d'où, vers où, combien. Un en-tête qui n'annonce que
                        la moitié de ce qu'il y a dessous se lit comme un défaut. */}
                    {onglet === "transport" && <th>Trajet</th>}
                    <th>Affecté</th>
                    <th>{peutAffecter ? "Ajouter" : ""}</th>
                  </tr>
                </thead>
                <tbody>
                  {besoins.map((besoin) => {
                    /*
                      La clé de SAUT ne porte pas la pastille, la clé React si : deux échéances
                      de la même ligne le même jour sont deux rangées, mais une seule source à
                      remonter. Voir `cleDuBesoin`.
                    */
                    const ancre = ancreDuPlan(jour.jour, cleDuBesoin(besoin));
                    return (
                    /*
                      La clé porte l'identifiant de la PASTILLE quand il en existe un : deux
                      livraisons de la même ligne le même jour — mille tonnes en deux échéances —
                      avaient jusqu'ici la même clé, et React n'en dessinait qu'une proprement.
                    */
                    <tr
                      key={
                        besoin.pastilleId ??
                        `${besoin.ligneId ?? besoin.poste}#${String(besoin.rang)}#${besoin.nature}`
                      }
                      {...{ [ATTRIBUT_CIBLE]: ancre }}
                      className={ancresCiblees.has(ancre) ? "cible" : undefined}
                    >
                      {/*
                        **Le poste remonte à la contre-étude** — *demande de Chris, 17/09/2026 :
                        « quand on arrive dans le plan de charge il faut pouvoir cliquer sur la
                        ligne et ouvrir en mode planning la contre-étude et arriver sur la ligne
                        concernée ».*

                        Sur la colonne du POSTE, et non sur la rangée entière : la rangée porte
                        déjà la liste des ressources affectées et le bouton qui en ajoute une.
                        Un clic qui changerait d'écran en partant de n'importe où ferait quitter
                        le plan de charge à chaque affectation manquée d'un demi-centimètre —
                        c'est le geste le plus fréquent de cet écran, il ne doit rien risquer.

                        Sans `ligneId`, rien à ouvrir : la ligne a été retirée de la
                        contre-étude, et le planning ne la montre plus (règle du 10/09/2026).
                        Le poste reste alors du texte, plutôt qu'un bouton qui échouerait.
                      */}
                      <td>
                        {onOuvrirLaLigne !== undefined && besoin.ligneId !== null ? (
                          <button
                            type="button"
                            className="lien"
                            title="Ouvrir cette ligne dans le planning de la contre-étude"
                            onClick={() => {
                              onOuvrirLaLigne(besoin);
                            }}
                          >
                            {besoin.poste}
                          </button>
                        ) : (
                          besoin.poste
                        )}
                        <span className="note"> {besoin.posteLibelle}</span>
                      </td>
                      <td>
                        <strong>{besoin.code}</strong>
                        <span className="note"> {besoin.libelle}</span>
                        {direLEffectif(besoin.unites) !== null && (
                          <span
                            className="plan__effectif"
                            title="Effectif déclaré sur la ligne de contre-étude. Les heures ci-contre sont celles de tous réunis."
                          >
                            {" "}
                            · {direLEffectif(besoin.unites)}
                          </span>
                        )}
                        {besoin.fournisseur !== null && (
                          <span className="note"> · {besoin.fournisseur}</span>
                        )}
                      </td>
                      {/*
                        Les heures sont celles de TOUS les exemplaires réunis — quatre
                        chauffeurs une demi-journée pèsent 14 h, pas 3,5. L'infobulle donne
                        la part de chacun : c'est elle que le responsable transport
                        compare à une journée de camion.

                        **Le total garde sa place, et la répartition vient dessous** —
                        *demande de Chris, 04/09/2026 : « on voit bien 4 h mais on n'a pas
                        l'info que c'est 2 h matin et 2 h après-midi ».* Le total est ce qui
                        se compare d'une ligne à l'autre et se somme en pied de tableau ; le
                        moment est ce qui décide si la même personne peut prendre autre chose
                        le même jour. Remplacer l'un par l'autre aurait rendu la colonne
                        insommable ; la règle de répartition est celle du planning des
                        ressources, lue au même endroit.
                      */}
                      <td
                        className="nombre"
                        title={
                          besoin.heures === null || besoin.unites === null || besoin.unites <= 1
                            ? undefined
                            : `${formaterHeure(besoin.heures)} h au total, soit ` +
                              `${formaterHeure(dec(besoin.heures).div(besoin.unites).toFixed())} h ` +
                              `pour chacun des ${String(besoin.unites)}`
                        }
                      >
                        {besoin.nature === "livraison"
                          ? `${formaterNombre(besoin.quantite ?? "0", 2)} ${besoin.unite}`
                          : `${formaterHeure(besoin.heures ?? "0")} h`}
                        {besoin.heureLimite !== null && (
                          <span className="note"> · {besoin.heureLimite}</span>
                        )}
                        {direLaRepartition(besoin.moments) !== null && (
                          <span className="note note-ligne">
                            {direLaRepartition(besoin.moments)}
                          </span>
                        )}
                        {/*
                          **Le moment d'une ÉCHÉANCE, pour le transport** — *demande de Chris,
                          04/09/2026 : « présente comme dans l'exploitation la colonne des heures
                          en spécifiant matin et après-midi, et fais pareil pour transport
                          acheminement articles ».*

                          La coupe de l'exploitation ne pouvait rien dire ici : une rotation et
                          une livraison ne portent aucune demi-journée, elles ont une heure
                          limite. Le tableau du transport restait donc muet là où celui de
                          l'exploitation parlait.

                          C'est l'heure limite qui répond, et la phrase le dit ainsi — « matin —
                          avant 12:00 » et non « matin » tout court : on annonce **quand le
                          camion est attendu**, jamais qu'il occupe la demi-journée.
                        */}
                        {besoin.moments.length === 0 &&
                          direLeMomentDeLEcheance(besoin.heureLimite) !== null && (
                            <span className="note note-ligne">
                              {direLeMomentDeLEcheance(besoin.heureLimite)}
                            </span>
                          )}
                        {/*
                          **Les heures chiffrées que les barres ne portent pas.**

                          *Conséquence de la décision de Chris du 04/09/2026 : le plan de
                          charge compte désormais les heures chiffrées, réparties en
                          remplissant les demi-journées posées. Dix heures sur deux
                          demi-journées n'y tiennent pas — 4 h + 4 h en laissent 2.*

                          Elles se disent, en rouge, plutôt que de disparaître : un plan de
                          charge plus léger que le chiffrage, sans un mot pour l'expliquer,
                          est exactement le genre d'écart que ce produit refuse de taire. Le
                          geste qui le referme est d'allonger la barre au planning.
                        */}
                        {/*
                          **Le contrôle du transport, sous la quantité.**

                          *Demande de Chris, 04/09/2026 : « fournir un contrôle du nombre
                          d'heures paramétré […] sur le tonnage il faut calculer le nombre de
                          tours ; cela fera le lien aussi entre les lignes transport et les
                          articles ».*

                          Posé ici et pas dans une colonne à part, parce qu'il ne se lit qu'avec
                          le tonnage sous les yeux : « 240 T » et « 15 voyages » sont la même
                          information dite deux fois, et les séparer obligerait à faire l'aller-
                          retour du regard sur chaque ligne.
                        */}
                        {aQuelqueChoseADire(besoin.controle) && (
                          <span className="note note-ligne plan__controle">
                            {/*
                              **Le calcul en toutes lettres, et non son seul résultat.**

                              *« Sois plus factuel — 80 tonnes = 17 tours de 20 minutes qui
                              correspondent aux heures évoquées »* (Chris, 04/09/2026). Chaque
                              terme est corrigible à un endroit différent : la distance à la
                              table des distances, la manœuvre au paramétrage, la charge utile
                              au référentiel des attelages. Un total seul n'apprend pas où
                              corriger.
                            */}
                            {direLeCalcul(besoin.controle) !== null && (
                              <span className="note-ligne">{direLeCalcul(besoin.controle)}</span>
                            )}
                            {direLEcart(besoin.controle) !== null && (
                              <span
                                className={
                                  tonDeLEcart(besoin.controle) === "ecart" ? "note--alerte" : undefined
                                }
                                title={
                                  besoin.controle.heuresAttendues === null
                                    ? undefined
                                    : `${formaterHeure(besoin.controle.heuresAttendues)} h attendues ` +
                                      `contre ${formaterHeure(besoin.controle.heuresChiffrees)} h chiffrées ` +
                                      "sur les lignes de transport de ce poste"
                                }
                              >
                                {" · "}
                                {direLEcart(besoin.controle)}
                              </span>
                            )}
                            {besoin.controle.incapacites.map((motif) => (
                              <span key={motif} className="note-ligne">
                                {direLIncapacite(motif)}
                              </span>
                            ))}
                          </span>
                        )}
                        {besoin.heuresNonPlacees !== "0" && dec(besoin.heuresNonPlacees).gt(0) && (
                          <span
                            className="note note-ligne note--alerte"
                            title="Ces heures sont chiffrées sur la ligne mais aucune barre ne les porte. Allonger la barre au planning pour les placer."
                          >
                            {formaterNombre(besoin.heuresNonPlacees, 1)} h non placées
                          </span>
                        )}
                      </td>

                      {/*
                        Qui l'apporte, et d'où. La phrase est fabriquée à un seul endroit
                        (`direLaProvenance`), le même qui sert au filtre « par nos soins » —
                        deux formulations finiraient par ne pas dire la même chose du même
                        acheminement.
                      */}
                      {onglet === "transport" && (
                        <td>
                          {/*
                            **Un seul porteur, coloré** — et non une pastille « à nous » posée à
                            côté d'une phrase qui dit déjà « par nos soins ». Mesuré à l'écran le
                            03/09/2026 : les deux ensemble donnaient « à nous par nos soins ».
                            Deux morceaux qu'il faut lire ensemble finissent de toute façon par
                            se contredire le jour où l'un des deux change.
                          */}
                          {/*
                            **Un km absent ne s'écrit pas, il s'explique** (09/09/2026).

                            Les motifs de `MOTIFS_SANS_KM` sont des phrases entières — « la table
                            de ce site ne porte pas encore cette commune » — qui tiendraient mal
                            dans une colonne réglée à 13 % de la largeur. Ils vont donc en
                            infobulle, comme sur le champ de la contre-étude. Un tiret à la place
                            aurait laissé croire à une distance nulle.

                            *La colonne passe à la ligne depuis le 16/09/2026 ; ce choix-ci tient
                            quand même — deux ou trois lignes sont faites pour un trajet, pas
                            pour une phrase d'explication.*
                          */}
                          <span
                            className={estParNosSoins(besoin) ? "plan__par-nos-soins" : "note"}
                            title={
                              besoin.km === null
                                ? (expliquerLAbsenceDeKm(besoin.motifSansKm) ?? undefined)
                                : besoin.kmEstForce
                                  ? "Kilométrage SAISI à la main sur la contre-étude : il l'emporte sur les tables de distances."
                                  : undefined
                            }
                          >
                            {direLaProvenance(besoin, (code) => nomDeLaCarriere.get(code) ?? null)}
                          </span>
                        </td>
                      )}

                      <td>
                        {/*
                          **Une livraison ne dit pas « personne ».**

                          Elle ne s'affecte pas — c'est la rotation qui porte le camion et le
                          chauffeur (décision de Chris, 03/09/2026). Lui appliquer le compte de
                          couverture ferait afficher « personne » en rouge sur une ligne où il
                          n'y a rien à faire, et le rouge cesserait de vouloir dire quelque
                          chose là où il compte.
                        */}
                        {besoin.nature === "livraison" ? (
                          <span className="note">à transporter</span>
                        ) : besoin.affectations.length === 0 ? (
                          <span className="en-ecart">
                            {direLaCouverture(0, besoin.unites).texte}
                          </span>
                        ) : (
                          <ul className="plan__affectations">
                            {besoin.affectations.map((a) => (
                              <li key={a.id}>
                                {a.nature === "materiel" && !qrRefuse && (
                                  <EtiquetteQr
                                    materielId={a.ressourceId}
                                    baseQr={baseQr}
                                    libelle={a.libelle}
                                    onRefus={() => {
                                      setQrRefuse(true);
                                    }}
                                  />
                                )}
                                {a.libelle}
                                {a.detail !== null && <span className="note"> · {a.detail}</span>}
                                {a.conflit.length > 0 && (
                                  <span className="en-ecart">
                                    {" "}
                                    · déjà{" "}
                                    {a.conflit
                                      .map(
                                        (ailleurs) =>
                                          `${direLesMoments(ailleurs.positions)} sur ${ailleurs.apo}`,
                                      )
                                      .join(", ")}
                                  </span>
                                )}
                                {/*
                                  **L'absence est signalée, pas défaite.** Une personne
                                  peut être affectée puis poser des congés : personne ne
                                  retire l'affectation à sa place, et la faire disparaître
                                  d'autorité ferait croire le besoin couvert. C'est la même
                                  règle que pour les conflits — l'écran dit, le responsable
                                  tranche.
                                */}
                                {(() => {
                                  const absence = absenceDe(a.ressourceId, besoin.date);
                                  return absence === undefined ? null : (
                                    <span className="en-ecart"> · {direLAbsence(absence)}</span>
                                  );
                                })()}
                                {peutAffecter && (
                                  <button
                                    type="button"
                                    className="lien"
                                    disabled={occupe !== null}
                                    onClick={() => {
                                      agir(retirerAffectationReelle(a.id), a.id);
                                    }}
                                  >
                                    retirer
                                  </button>
                                )}
                              </li>
                            ))}
                            {/*
                              Le compte, sous les noms : sur une ligne à quatre chauffeurs,
                              un seul nom se lit comme un besoin couvert. C'est la même
                              règle que le signalement d'absence — l'écran dit ce qui
                              manque, il ne défait ni ne bloque rien.
                            */}
                            {(() => {
                              const couverture = direLaCouverture(
                                besoin.affectations.length,
                                besoin.unites,
                              );
                              return couverture.texte === "" ? null : (
                                <li className={couverture.complet ? "note" : "en-ecart"}>
                                  {couverture.texte}
                                </li>
                              );
                            })()}
                          </ul>
                        )}
                      </td>
                      <td>
                        {/*
                          Pas de liste de choix sur une livraison : un seul endroit dit qui
                          transporte quoi, et c'est la rotation. En offrir une ici permettrait
                          d'affecter le même transport deux fois sans que rien ne dise laquelle
                          des deux réponses fait foi.
                        */}
                        {peutAffecter && besoin.ligneId !== null && besoin.nature !== "livraison" && (
                          <select
                            value=""
                            disabled={occupe !== null}
                            onChange={(e) => {
                              if (e.target.value === "") return;
                              const choix = tousLesCandidats(proposables(besoin)).find(
                                (p) => p.id === e.target.value,
                              );
                              if (choix === undefined) return;
                              const cle = `${besoin.ligneId ?? ""}#${String(besoin.jourRelatif)}`;
                              setNoteAssociee(null);
                              agir(
                                affecterRessource({
                                  chantierId: besoin.chantierId,
                                  ligneId: besoin.ligneId ?? "",
                                  jourRelatif: besoin.jourRelatif,
                                  nature: choix.nature,
                                  ressourceId: choix.id,
                                }).then((r) => {
                                  /*
                                    Deux phrases, une seule note.

                                    Le véhicule associé (077) et les journées suivantes
                                    (15/09/2026) sont deux automatismes du même geste : les
                                    annoncer à deux endroits ferait chercher le second, et en
                                    afficher un seul ferait croire que l'autre n'a rien fait.
                                  */
                                  const texte = [direLAssociation(r.associe), direLaSerie(r.serie)]
                                    .filter((p): p is string => p !== null)
                                    .join(" ");
                                  setNoteAssociee(texte === "" ? null : { cle, texte });
                                }),
                                cle,
                              );
                            }}
                          >
                            <option value="">— affecter —</option>
                            {/*
                              **Le groupe nomme la provenance quand ce n'est pas le code exact.**

                              Une liste dont on ne sait pas ce qu'elle contient est le défaut
                              d'origine (Chris, 03/09/2026) : 216 machines ressemblaient à un
                              filtre absent, et deux niveleuses ressembleraient à un parc qui
                              n'en compte que deux. Le titre dit ce qui manquait — aucune
                              ressource sur ce code — et d'où viennent les propositions.

                              **« Autres ressources » vient EN DESSOUS, et ne déplace rien**
                              (Chris, 10/09/2026 : *« tu laisses la présentation telle quelle est
                              dans le menu déroulant »*). La liste filtrée garde donc sa forme,
                              options nues et sans titre : coiffer les trois noms attendus d'un
                              en-tête pour rendre le second groupe symétrique changerait ce qu'on
                              lit en premier, alors que le sujet est ce qu'on trouve en dernier.
                            */}
                            {(() => {
                              const proposition = proposables(besoin);
                              const enOptions = (liste: readonly CandidatAffectable[]) =>
                                liste.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.libelle}
                                  </option>
                                ));
                              const principal = enOptions(proposition.candidats);
                              return (
                                <>
                                  {proposition.titre === null ? (
                                    principal
                                  ) : (
                                    <optgroup label={proposition.titre}>{principal}</optgroup>
                                  )}
                                  {proposition.extension !== null && (
                                    <optgroup label={proposition.extension.titre}>
                                      {enOptions(proposition.extension.candidats)}
                                    </optgroup>
                                  )}
                                </>
                              );
                            })()}
                          </select>
                        )}
                        {/*
                          **Ce que le matériel associé est devenu, sous le menu qui l'a déclenché.**

                          Une note, jamais une alerte : rien n'a échoué, la personne est affectée.
                          Elle vit ici et pas en haut de l'écran — c'est le défaut que Chris a
                          rencontré au premier essai, le 10/09/2026 : la phrase existait, à
                          plusieurs écrans de défilement de la ligne qu'il regardait.
                        */}
                        {noteAssociee !== null &&
                          noteAssociee.cle ===
                            `${besoin.ligneId ?? ""}#${String(besoin.jourRelatif)}` && (
                            <p className="note" role="status">
                              {noteAssociee.texte}
                            </p>
                          )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total du chantier ce jour</td>
                    <td className="nombre">
                      {formaterHeure(
                        besoins.reduce((s, b) => s.add(dec(b.heures ?? "0")), dec(0)).toFixed(),
                      )}{" "}
                      h
                    </td>
                    {/*
                      **Les quantités ne se totalisent pas**, et la case reste donc vide sous la
                      provenance : les livraisons d'un même jour se comptent en tonnes, en mètres
                      cubes et à l'unité. « 62 » sous une colonne qui mélange les trois serait un
                      nombre que personne ne pourrait rapprocher de rien.
                    */}
                    {onglet === "transport" && <td />}
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
        </article>
      ))}

    </section>
  );
}
